import React, { useState, useEffect, useRef } from 'react'
import { T, BottomNav, Spinner, Empty, Stat, SectionLabel, SearchBar, searchMatch,
         NHL_MARKETS, MARKET_LABEL, RECO, fmtOdds, pct, tierRank, CarryoverChip } from './ui'
import { PickCard, LineLegend } from './cards'
import { shareTop, shareProof, shareNhlTop10, shareNhlBest, shareNhlMarket, shareNhlSpots, shareNhlRecap } from './share'
import PlayerModal from './PlayerModal'
import GameModal from './GameModal'
import { SlipButton, SlipSheet, legKey } from './Parlay'
import * as api from './api'

// "Today" in America/New_York — the slate/settlement clock. Using the browser's
// local date would show an empty board to a user ahead of ET (e.g. Europe late at
// night requests tomorrow, which has no slate yet).
const todayISO = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
  } catch {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
}

export default function NHLApp() {
  const [gate, setGate] = useState('checking')   // checking | locked | open
  const [view, setView] = useState('home')
  const [date, setDate] = useState(todayISO())
  // Board filter state lives at the shell so Spots/Games cards can deep-link into it.
  const [market, setMarket] = useState('shots')
  const [q, setQ] = useState('')
  const [player, setPlayer] = useState(null)   // tapped play -> detail sheet
  const [game, setGame] = useState(null)       // tapped game -> game drill-in
  const [slip, setSlip] = useState([])         // parlay legs
  const [slipOpen, setSlipOpen] = useState(false)
  const [cardsOpen, setCardsOpen] = useState(false)
  const jumpTo = (mk, query = '') => { if (mk) setMarket(mk); setQ(query || ''); setView('board') }
  const addLeg = p => setSlip(s => s.some(l => legKey(l) === legKey(p)) ? s : [...s, {
    player_id: p.player_id, market: p.market, name: p.name, team: p.team, opponent: p.opponent,
    line: p.book_line != null ? p.book_line : p.model_line, prob_over: p.prob_over, game_id: p.game_id }])
  const removeLeg = l => setSlip(s => s.filter(x => legKey(x) !== legKey(l)))
  const slipKeys = new Set(slip.map(legKey))

  useEffect(() => { api.checkNHLAccess().then(setGate) }, [])

  if (gate === 'checking') {
    return <Shell><Spinner /></Shell>
  }
  if (gate === 'locked') {
    return <Shell><LockScreen /></Shell>
  }
  return (
    <Shell>
      <Header date={date} setDate={setDate} onShare={() => setCardsOpen(true)} />
      <div style={{ padding: '0 12px 84px' }}>
        {view === 'home'   && <HomeView date={date} onPlayer={setPlayer} setView={setView} jumpTo={jumpTo} />}
        {view === 'board'  && <BoardView date={date} market={market} setMarket={setMarket} q={q} setQ={setQ} onPlayer={setPlayer} />}
        {view === 'games'  && <GamesView date={date} onGame={setGame} />}
        {view === 'spots'  && <SpotsView date={date} jumpTo={jumpTo} />}
        {view === 'record' && <RecordView date={date} />}
      </div>
      <BottomNav view={view} setView={setView} />
      <Compliance />
      {game && <GameModal g={game} date={date} onClose={() => setGame(null)}
        onPlayer={p => { setGame(null); setPlayer(p) }} />}
      {player && <PlayerModal p={player} date={date} onClose={() => setPlayer(null)}
        addLeg={addLeg} inSlip={slipKeys.has(legKey(player))} />}
      {cardsOpen && <CardsSheet date={date} onClose={() => setCardsOpen(false)} />}
      <SlipButton slip={slip} onOpen={() => setSlipOpen(true)} />
      {slipOpen && <SlipSheet slip={slip} onRemove={removeLeg}
        onClear={() => { setSlip([]); setSlipOpen(false) }} onClose={() => setSlipOpen(false)} />}
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div style={{ background: T.bg, minHeight: '100vh', maxWidth: 460, margin: '0 auto',
      position: 'relative', color: T.text, fontFamily: 'system-ui, sans-serif' }}>
      {children}
    </div>
  )
}

function Header({ date, setDate, onShare }) {
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 50, background: T.bg,
      borderBottom: `1px solid ${T.border}`, padding: '10px 12px 10px' }}>
      <a href="?" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5,
        fontWeight: 700, color: T.muted2, textDecoration: 'none', marginBottom: 6 }}>‹ All sports</a>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 900, fontSize: 20, letterSpacing: '-0.03em',
          display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>LINE<span style={{ color: T.green }}>BRKR</span></span>
          <span style={{ marginLeft: 7, fontSize: 13, color: T.ice, fontWeight: 800 }}>🏒 NHL</span>
          <span style={{ marginLeft: 7, fontSize: 9.5, fontWeight: 900, letterSpacing: '0.08em',
            color: '#1A1205', background: T.amber, borderRadius: 5, padding: '2px 6px' }}>BETA</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={onShare} title="Share a board" style={{ background: T.greenDim, color: T.green,
            border: `1px solid ${T.green}66`, borderRadius: 8, padding: '6px 9px', fontSize: 12, fontWeight: 800,
            cursor: 'pointer' }}>↗ Share</button>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ background: T.card, color: T.text, border: `1px solid ${T.border}`,
              borderRadius: 8, padding: '6px 8px', fontSize: 12.5, fontFamily: T.mono }} />
        </div>
      </div>
      <div style={{ marginTop: 7, fontSize: 10.5, color: T.amber, fontWeight: 700,
        lineHeight: 1.4 }}>
        ⚠ NHL is in testing — models are still calibrating on live results. Research, not advice.
      </div>
    </div>
  )
}

// "Share a board" — the NFL CardsSheet: model-ranked graphics for the selected date,
// opened from the header. Each opens the native share sheet on mobile (PNG download on
// desktop); a card with nothing honest to show says why instead of drawing filler.
const prevDay = d => { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() - 1)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}` }
function CardsSheet({ date, onClose }) {
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState({})
  const run = (key, fn) => async () => {
    if (busy) return
    setBusy(key); setMsg(m => ({ ...m, [key]: '' }))
    try { await fn() } catch (e) { setMsg(m => ({ ...m, [key]: e?.message || 'Could not build this card.' })) }
    setBusy('')
  }
  const cards = [
    ['best', '🎯 Best Bets', 'Plays where the model beats the book price', () => shareNhlBest(date)],
    ['top10', '🏒 Top 10 Board', 'Every market, ranked by model grade', () => shareNhlTop10(date)],
    ['shots', '🎯 Shots on Goal', 'Top shots plays · hit % + best price', () => shareNhlMarket('shots', date)],
    ['goals', '🚨 Light the Lamp', 'Most likely goal scorers · anytime price', () => shareNhlMarket('goals', date)],
    ['points', '⭐ Points', 'Top points plays', () => shareNhlMarket('points', date)],
    ['spots', '📈 Matchup Spots', 'Shootouts, weak PKs, goalie workloads, back-to-backs', () => shareNhlSpots(date)],
    ['recap', '📊 Last Night’s Results', 'Graded best bets — what cashed & what missed', () => shareNhlRecap(prevDay(date))],
    ['record', '🏆 The Record', 'Season record, graded at the book line', () => shareProof()],
  ]
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 320,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg, width: '100%', maxWidth: 460, maxHeight: '90vh',
        overflowY: 'auto', borderRadius: '18px 18px 0 0', border: `1px solid ${T.border}`, padding: '18px 16px 28px' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border2, margin: '0 auto 14px' }} />
        <div style={{ fontSize: 17, fontWeight: 900, color: T.text, marginBottom: 3 }}>Share a board</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 14 }}>Model-ranked graphics for {date} — post to X, IG or the group chat.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {cards.map(([k, title, sub, fn]) => (
            <button key={k} onClick={run(k, fn)} disabled={!!busy} style={{ textAlign: 'left',
              cursor: busy ? 'default' : 'pointer', background: T.card, border: `1px solid ${T.border2}`,
              borderRadius: 12, padding: '13px 14px', color: T.text, opacity: busy && busy !== k ? 0.5 : 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800 }}>{busy === k ? 'Building…' : title}</div>
              <div style={{ fontSize: 11.5, color: msg[k] ? T.amber : T.muted, marginTop: 2 }}>{msg[k] || sub}</div>
            </button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: T.faint, textAlign: 'center', marginTop: 14 }}>Research, not advice · 21+</div>
      </div>
    </div>
  )
}

function LockScreen() {
  return (
    <div style={{ padding: '60px 24px', textAlign: 'center', color: T.muted }}>
      <div style={{ fontSize: 30, marginBottom: 10 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Access code required</div>
      <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
        Enter your LINEBRKR access code in the app to unlock the NHL board.
        The public track record is at <b style={{ color: T.green }}>/nhl/proof</b>.
      </div>
    </div>
  )
}

function Compliance() {
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, pointerEvents: 'none',
      textAlign: 'center', fontSize: 9, color: T.faint, paddingBottom: 2 }} />
  )
}

// ── Home (day at a glance) ─────────────────────────────────────────────────────
function HomeView({ date, onPlayer, setView }) {
  const [plays, setPlays] = useState(null)
  const [total, setTotal] = useState(0)
  const [live, setLive] = useState(false)
  const [early, setEarly] = useState(false)
  const [sc, setSc] = useState(null)
  const [games, setGames] = useState(null)
  useEffect(() => {
    let alive = true
    setPlays(null)
    // No market filter -> the leaderboard returns ALL markets, recommended-first.
    // 250 so the best-bets slice is never truncated; r.count is the true slate total.
    api.getNHLLeaderboard('', date, 250).then(r => {
      if (alive) { setPlays(r.plays || []); setTotal(r.count || (r.plays || []).length); setLive(!!r.live); setEarly(!!r.early_season) }
    })
    api.getNHLScorecard().then(r => { if (alive) setSc(r) })
    api.getNHLSlate(date).then(r => { if (alive) setGames(r.games || []) })
    return () => { alive = false }
  }, [date])

  if (plays === null) return <Spinner />
  const ranked = plays.filter(p => RECO.has(p.tier))
  const hasBest = ranked.length > 0
  const rec = sc?.recommended || {}
  const nGames = games ? games.length : null

  return (
    <div style={{ paddingTop: 12 }}>
      {early && <CarryoverChip />}
      {/* best-bets banner — the headline answer to "what should I look at" */}
      <div style={{ background: hasBest ? `linear-gradient(180deg, ${T.greenDk}, ${T.card})` : `linear-gradient(180deg, ${T.card3}, ${T.card})`,
        border: `1px solid ${hasBest ? T.green + '66' : T.border}`, borderRadius: 14, padding: '13px 15px', marginBottom: 12 }}>
        <div style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: '0.12em', color: hasBest ? T.green : T.ice }}>
          {hasBest ? '🎯 TODAY’S BEST BETS' : 'TODAY’S READ'}
        </div>
        <div style={{ fontSize: hasBest ? 23 : 15, fontWeight: 900, color: T.text, marginTop: 5, lineHeight: 1.15 }}>
          {hasBest ? `${ranked.length} best bet${ranked.length === 1 ? '' : 's'}` : 'No priced best bets yet'}
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4, lineHeight: 1.45 }}>
          {hasBest
            ? <>Model beats the book. {nGames != null ? `${nGames} game${nGames === 1 ? '' : 's'} · ` : ''}{total} projections{live ? <span style={{ color: T.red, fontWeight: 800 }}> · ● LIVE</span> : ''}</>
            : <>Books post lines closer to puck drop — a “best bet” needs a real edge vs the price, so there are none yet. The model’s top reads are below.{nGames != null ? ` ${nGames} game${nGames === 1 ? '' : 's'} today.` : ''}</>}
        </div>
      </div>

      {(rec.wins || rec.losses) ? (
        <div onClick={() => setView('record')} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', background: T.greenDim, border: `1px solid ${T.green}55`, borderRadius: 12,
          padding: '10px 13px', margin: '0 0 12px' }}>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: '0.1em', color: T.green }}>TRACK RECORD</div>
            <div style={{ fontSize: 11.5, color: T.muted2, marginTop: 2 }}>Recommended · graded at the book line</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 16, color: T.text }}>
              {rec.wins || 0}-{rec.losses || 0}{rec.pushes ? `-${rec.pushes}` : ''}
            </div>
            <div style={{ fontSize: 10.5, color: T.green }}>{rec.win_pct != null ? pct(rec.win_pct) : '—'} · full record →</div>
          </div>
        </div>
      ) : null}

      {hasBest ? (
        <>
          <SectionLabel right="Full board ›" onRight={() => setView('board')}>🎯 Best bets</SectionLabel>
          {ranked.slice(0, 15).map((p, i) =>
            <PickCard key={`${p.player_id}-${p.market}-${i}`} p={p} onClick={() => onPlayer(p)} />)}
        </>
      ) : (
        <>
          <SectionLabel right="Full board ›" onRight={() => setView('board')}>Model’s top reads</SectionLabel>
          <div style={{ fontSize: 10.5, color: T.faint, margin: '0 2px 8px', lineHeight: 1.45 }}>
            Highest model probability — not edge-backed best bets yet (no posted prices). Tap any for the breakdown.
          </div>
          {plays.length === 0 ? <Empty message="No projections for this date yet." /> :
            plays.slice(0, 12).map((p, i) =>
              <PickCard key={`${p.player_id}-${p.market}-${i}`} p={p} onClick={() => onPlayer(p)} />)}
        </>
      )}
    </div>
  )
}

// ── Board ────────────────────────────────────────────────────────────────────
function BoardView({ date, market, setMarket, q, setQ, onPlayer }) {
  const [rows, setRows] = useState(null)
  const [live, setLive] = useState(false)
  const [early, setEarly] = useState(false)
  const [bestOnly, setBestOnly] = useState(true)   // lead with best bets, All on tap
  const liveRef = useRef(false)                     // current live state for the poller

  useEffect(() => {
    let alive = true
    setRows(null)   // only on a market/date change — the 60s poll reuses the view
    const load = () => api.getNHLLeaderboard(market, date).then(r => {
      if (!alive) return
      setRows(r.plays || [])
      setLive(!!r.live)
      setEarly(!!r.early_season)
      liveRef.current = !!r.live
    })
    load()
    // While any game is live, refresh the board every 60s so the in-game hit/miss
    // ✓/✗ ticks without a manual reload. The poll reads liveRef (not a dep), so a
    // live toggle re-polls in place instead of re-running the effect and flashing
    // the spinner.
    const id = setInterval(() => { if (liveRef.current) load() }, 60000)
    return () => { alive = false; clearInterval(id) }
  }, [market, date])

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', padding: '12px 0 4px' }}>
        {NHL_MARKETS.map(m => {
          const on = m.key === market
          return (
            <button key={m.key} onClick={() => setMarket(m.key)} style={{ flexShrink: 0, padding: '7px 12px',
              borderRadius: 20, border: `1px solid ${on ? T.green : T.border}`, cursor: 'pointer',
              background: on ? T.greenDim : T.card, color: on ? T.green : T.muted,
              fontSize: 12, fontWeight: on ? 800 : 600 }}>{m.label}</button>
          )
        })}
      </div>
      <SearchBar value={q} onChange={setQ} />
      {early && <CarryoverChip />}
      {live && (
        <div style={{ fontSize: 10.5, fontWeight: 800, color: T.red, letterSpacing: '0.06em',
          margin: '2px 2px 8px' }}>● LIVE — hit/miss updating every 60s</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '2px 2px 8px', gap: 8 }}>
        <div style={{ display: 'flex', gap: 3, background: T.card2, borderRadius: 9, padding: 2 }}>
          {[['best', '🎯 Best bets'], ['all', 'All']].map(([k, lab]) => {
            const on = (k === 'best') === bestOnly
            return (
              <button key={k} onClick={() => setBestOnly(k === 'best')} style={{ padding: '5px 11px', borderRadius: 7,
                border: 'none', cursor: 'pointer', background: on ? T.green : 'transparent',
                color: on ? '#04140D' : T.muted2, fontSize: 11, fontWeight: 800 }}>{lab}</button>
            )
          })}
        </div>
        {rows && rows.length > 0 && (
          <button onClick={() => shareTop(market, date)} style={{ flexShrink: 0,
            background: T.greenDim, color: T.green, border: `1px solid ${T.green}66`, borderRadius: 8,
            padding: '6px 11px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            ↗ Share top
          </button>
        )}
      </div>
      {!bestOnly && <LineLegend />}
      {rows === null ? <Spinner /> : (() => {
        if (!rows.length) return <Empty message="No projections for this date yet." />
        const shown = rows.filter(searchMatch(q, r => [r.name, r.team, r.opponent]))
          .filter(p => !bestOnly || RECO.has(p.tier))
        if (!shown.length) return <Empty message={bestOnly
          ? 'No best bets in this market yet — tap All to see every projection.'
          : 'No plays match your search.'} />
        return shown.map((p, i) =>
          <PickCard key={`${p.player_id}-${p.market}-${i}`} p={p} onClick={() => onPlayer(p)} />)
      })()}
    </div>
  )
}

// ── Games (slate) ────────────────────────────────────────────────────────────
function GamesView({ date, onGame }) {
  const [games, setGames] = useState(null)
  const [plays, setPlays] = useState([])
  useEffect(() => {
    let alive = true
    setGames(null); setPlays([])
    api.getNHLSlate(date).then(r => { if (alive) setGames(r.games || []) })
    api.getNHLLeaderboard('', date, 250).then(r => { if (alive) setPlays(r.plays || []) })
    return () => { alive = false }
  }, [date])
  if (games === null) return <Spinner />
  if (!games.length) return <Empty message="No games scheduled for this date." />

  const inGame = g => plays.filter(p =>
    (g.game_id && p.game_id === g.game_id) || p.team === g.home || p.team === g.away)
  // Enrich each game with its best-bet count + top play, then lead with the most actionable.
  const enriched = games.map(g => {
    const ps = inGame(g)
    // Rank by conviction (tier) first, prob only as the tie-break — a mixed-market
    // game's "top read" must be the best PLAY, not whatever market has the highest
    // raw probability (always shots/saves otherwise).
    const byTier = ps.slice().sort((a, b) =>
      tierRank(a.tier) - tierRank(b.tier) || (b.prob_over || 0) - (a.prob_over || 0))
    const best = byTier.filter(p => RECO.has(p.tier))
    return { g, nBest: best.length, top: best[0] || byTier[0] || null }
  }).sort((a, b) => b.nBest - a.nBest || tierRank(a.top?.tier) - tierRank(b.top?.tier)
      || (b.top?.prob_over || 0) - (a.top?.prob_over || 0))

  return (
    <div style={{ paddingTop: 12 }}>
      {enriched.map(({ g, nBest, top }) => (
        <div key={g.game_id} onClick={() => onGame(g)}
          style={{ background: T.card, border: `1px solid ${nBest > 0 ? T.green + '55' : T.border}`,
          borderRadius: 12, padding: '12px', marginBottom: 8, cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{g.away} @ {g.home}</span>
            <span style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
              {g.state}{g.home_score != null ? ` · ${g.away_score}-${g.home_score}` : ''}
            </span>
          </div>

          {/* best-bet highlight row — the "premier" signal */}
          {top && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, flexWrap: 'wrap' }}>
              {nBest > 0 ? (
                <span style={{ fontSize: 9.5, fontWeight: 900, color: '#04140D', background: T.green,
                  borderRadius: 5, padding: '2px 7px', letterSpacing: '0.04em' }}>🎯 {nBest} BEST BET{nBest === 1 ? '' : 'S'}</span>
              ) : (
                <span style={{ fontSize: 9.5, fontWeight: 800, color: T.muted2, background: T.card2,
                  borderRadius: 5, padding: '2px 7px', letterSpacing: '0.04em' }}>TOP READ</span>
              )}
              <span style={{ fontSize: 11.5, color: T.muted2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {top.name} · {MARKET_LABEL[top.market] || top.market}
                <b style={{ color: nBest > 0 ? T.green : T.text, marginLeft: 5 }}>{pct(top.prob_over)}</b>
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11.5, color: T.muted2, flexWrap: 'wrap' }}>
            {g.total_line != null && <span>Total <b style={{ color: T.text }}>{g.total_line}</b></span>}
            {g.away_ml != null && <span>{g.away} <b style={{ color: T.text }}>{fmtOdds(g.away_ml)}</b></span>}
            {g.home_ml != null && <span>{g.home} <b style={{ color: T.text }}>{fmtOdds(g.home_ml)}</b></span>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
            <span style={{ fontSize: 11.5, color: T.muted }}>
              🥅 {g.away_goalie || 'TBD'} · {g.home_goalie || 'TBD'}
            </span>
            <span style={{ fontSize: 11, color: T.ice, fontWeight: 700, flexShrink: 0 }}>Open ›</span>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Spots (matchup angles) ────────────────────────────────────────────────────
function SpotsView({ date, jumpTo }) {
  const [sig, setSig] = useState(null)
  useEffect(() => { setSig(null); api.getNHLSignals(date).then(setSig) }, [date])
  if (sig === null) return <Spinner />
  const empty = !(sig.shootouts?.length || sig.weak_pk?.length || sig.back_to_backs?.length || sig.goalie_spots?.length)
  if (empty) return <Empty message="No standout spots for this date." />
  const Card = ({ icon, title, sub, onClick }) => (
    <div onClick={onClick} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: '11px 12px', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'center',
      cursor: onClick ? 'pointer' : 'default' }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: T.text }}>{title}</div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{sub}</div>
      </div>
      {onClick && <span style={{ fontSize: 18, color: T.muted, flexShrink: 0 }}>›</span>}
    </div>
  )
  // "NYI vs TOR" -> ["NYI","TOR"]; filtering the board by either team code shows BOTH
  // sides of the game (team match + opponent match), which is what a game-level spot wants.
  const teams = s => (s || '').split(/\s*vs\s*/i).map(t => t.trim()).filter(Boolean)
  return (
    <div style={{ paddingTop: 12 }}>
      {sig.shootouts?.length > 0 && <SectionLabel>Shootout spots (skaters over)</SectionLabel>}
      {sig.shootouts?.map((g, i) => (
        <Card key={`s${i}`} icon="🔥" title={g.game}
          sub={`Game total ${g.total} — pace up, skater props tilt over`}
          onClick={() => jumpTo('points', teams(g.game)[0])} />
      ))}
      {sig.weak_pk?.length > 0 && <SectionLabel>Power-play targets (weak PK)</SectionLabel>}
      {sig.weak_pk?.map((w, i) => (
        <Card key={`p${i}`} icon="⚡" title={`${w.team} vs ${w.opp}`}
          sub={`${w.opp} PK ${w.opp_pk_pct}% — power-play points up`}
          onClick={() => jumpTo('pp_points', w.team)} />
      ))}
      {sig.goalie_spots?.length > 0 && <SectionLabel>Goalie saves spots</SectionLabel>}
      {sig.goalie_spots?.map((g, i) => (
        <Card key={`g${i}`} icon="🥅" title={`${g.goalie || g.team} (${g.team})`}
          sub={`vs ${g.opp} — ${g.opp_shots_pg} shots/g, saves over`}
          onClick={() => jumpTo('saves', g.goalie || g.team)} />
      ))}
      {sig.back_to_backs?.length > 0 && <SectionLabel>Back-to-back fades</SectionLabel>}
      {sig.back_to_backs?.map((t, i) => (
        <Card key={`b${i}`} icon="💤" title={t}
          sub="2nd of a back-to-back — fatigue, lean unders"
          onClick={() => jumpTo('shots', t)} />
      ))}
      <div style={{ fontSize: 10.5, color: T.faint, marginTop: 14, lineHeight: 1.5 }}>
        Tap a spot to open the matching players on the board. Spots are matchup angles from
        schedule totals + team rates — a starting point, not a play. Research, not advice · 21+.
      </div>
    </div>
  )
}

// ── Record ───────────────────────────────────────────────────────────────────
function RecordView() {
  const [sc, setSc] = useState(null)
  const [tiers, setTiers] = useState(null)
  useEffect(() => {
    api.getNHLScorecard().then(setSc)
    api.getNHLTierRecord().then(r => setTiers(r.tiers || {}))
  }, [])
  if (sc === null) return <Spinner />
  const rec = sc.recommended || {}
  const real = sc.realized || {}
  const wl = r => `${r.wins || 0}-${r.losses || 0}${r.pushes ? `-${r.pushes}` : ''}`
  return (
    <div style={{ paddingTop: 14 }}>
      <SectionLabel>Recommended record (graded at the book line)</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        <Stat label="Recommended" value={wl(rec)} color={T.green} />
        <Stat label="Win %" value={rec.win_pct != null ? pct(rec.win_pct) : '—'} color={T.text} />
        <Stat label="Plays" value={sc.n_recommended || 0} color={T.text} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 8 }}>
        <Stat label="Realized (book)" value={wl(real)} color={real.win_pct >= 0.5 ? T.green : T.amber} />
        <Stat label="CLV+" value={sc.clv_positive_rate != null ? pct(sc.clv_positive_rate) : '—'} color={T.ice} />
        <Stat label="Mean CLV" value={sc.clv_mean != null ? sc.clv_mean : '—'} color={T.text} />
      </div>

      <SectionLabel>By market (realized book-line)</SectionLabel>
      {Object.entries(sc.by_prop || {}).filter(([, v]) => (v.realized?.graded || 0) > 0).map(([mk, v]) => (
        <div key={mk} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px',
          borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ color: T.muted2 }}>{MARKET_LABEL[mk] || mk}</span>
          <span style={{ fontFamily: T.mono }}>{wl(v.realized)} · {v.realized.win_pct != null ? pct(v.realized.win_pct) : '—'}</span>
        </div>
      ))}

      <SectionLabel>By conviction tier</SectionLabel>
      {tiers && Object.keys(tiers).length ? Object.entries(tiers).map(([t, b]) => (
        <div key={t} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px',
          borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ color: RECO.has(t) ? T.green : T.muted2, fontWeight: RECO.has(t) ? 800 : 600 }}>{t}</span>
          <span style={{ fontFamily: T.mono }}>{b.wins}-{b.losses} · {b.win_pct != null ? pct(b.win_pct) : '—'}</span>
        </div>
      )) : <Empty message="No settled results yet." />}
      <div style={{ fontSize: 10.5, color: T.faint, marginTop: 14, lineHeight: 1.5 }}>
        The advertised record counts only recommended plays (Strong / Top Rated / Elite),
        over-only, graded at the real closing book line — never a model line. Research, not advice · 21+.
      </div>
    </div>
  )
}
